import { Component, computed, inject, input, output } from '@angular/core';
import type { ModalAction } from '@purolator/ui-kit-angular';
import { ModalComponent, TypographyComponent } from '@purolator/ui-kit-angular';
import { TranslatePipe, TranslationService } from '../../../core/i18n';

/**
 * Reusable warning confirmation `puro-modal` with secondary cancel and primary confirm actions.
 * Callers supply i18n keys and a BEM modal class prefix for host-scoped styling.
 */
@Component({
  selector: 'app-warning-confirm-dialog',
  imports: [ModalComponent, TranslatePipe, TypographyComponent],
  templateUrl: './warning-confirm-dialog.html',
  styleUrl: './warning-confirm-dialog.scss',
})
export class WarningConfirmDialog {
  private readonly translation = inject(TranslationService);

  readonly open = input(false);
  /** Dot-notated i18n key for the modal title. */
  readonly titleKey = input.required<string>();
  /** Dot-notated i18n key for the modal body. */
  readonly bodyKey = input.required<string>();
  /** Dot-notated i18n key for the secondary (cancel) action. */
  readonly cancelKey = input.required<string>();
  /** Dot-notated i18n key for the primary (confirm) action. */
  readonly confirmKey = input.required<string>();
  /**
   * BEM block name used for modal/body CSS classes (e.g. `logout-confirmation-dialog`).
   * Yields `{modalClass}__modal` and `{modalClass}__body`.
   */
  readonly modalClass = input.required<string>();

  readonly cancelled = output<void>();
  readonly confirmed = output<void>();

  protected readonly modalCssClass = computed(() => `${this.modalClass()}__modal`);
  protected readonly bodyCssClass = computed(() => `${this.modalClass()}__body`);

  protected readonly modalTitle = computed(() => {
    this.translation.revision();
    return this.translation.translate(this.titleKey());
  });

  protected readonly actions = computed<ModalAction[]>(() => {
    this.translation.revision();
    return [
      {
        label: this.translation.translate(this.cancelKey()),
        variant: 'secondary',
        onClick: () => this.onCancel(),
      },
      {
        label: this.translation.translate(this.confirmKey()),
        variant: 'primary',
        onClick: () => this.onConfirm(),
      },
    ];
  });

  /**
   * Dismisses the dialog without confirming.
   */
  protected onCancel(): void {
    this.cancelled.emit();
  }

  /**
   * Confirms the warning action for the parent flow.
   */
  protected onConfirm(): void {
    this.confirmed.emit();
  }
}
